import React, { useCallback, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { ChevronRight } from '@mui/icons-material';

import {
  Button, Card, FormControl, Typography
} from '@mui/material';

import {
  Controller,
  UseFormReturn,
  useForm,
  useWatch
} from 'react-hook-form';

import Label from '@/shared/molecules/Label';
import localizationService from '@/services/localizationService';

import css from './StoreSelector.module.css';

export const MINIMUM_ZIPCODE_LENGTH = 5;
export interface StoreAddress {
  id?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  storeNumber?: string;
}

interface StoreSelectorProps {
  id: string;
  name: string;
  control?: UseFormReturn['control'];
  defaultValue?: string;
  required?: boolean;
  onSelectStore?: (store: StoreAddress) => void;
  variant?: 'standard' | 'outlined' | 'filled';
  loadingText?: string;
  size?: 'small' | 'medium';
  noOptionsText?: string;
}

export default function StoreSelector({
  id,
  name,
  control,
  defaultValue,
  required,
  onSelectStore,
  variant = 'outlined',
  loadingText = 'Searching...',
  size = 'small',
  noOptionsText = 'No stores found'
}: StoreSelectorProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<StoreAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<string>('');
  const { control: defaultControl } = useForm();
  const baseControl = control || defaultControl;
  const inputRef = useRef<HTMLInputElement>(null);

  const watchedValue = useWatch({
    control: baseControl,
    name
  });

  const search = useCallback(async (inputValue?: string) => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const { found = false, stores = [] } = await localizationService?.findCarryoutStoresV2?.({ zipcode: inputValue });

      if (found && stores?.length > 0) {
        setOptions(stores?.map((store: StoreAddress) => ({
          address: store?.address,
          city: store?.city,
          state: store?.state,
          zipcode: store?.zipcode,
          storeNumber: store?.storeNumber
        })));

        setOpen(true);
      }

      setLoading(false);
    } catch (error) {
      setOptions([]);
      setLoading(false);
    }
  }, [loading]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: newValue = '' } = event?.target;

    setValue(newValue);

    if (newValue?.length >= MINIMUM_ZIPCODE_LENGTH) {
      search(newValue);
    } else {
      // clear options if the input length is less than MINIMUM_ZIPCODE_LENGTH
      setOptions([]);
    }
  };

  return (
    <>
      <Label htmlFor={id} className={css.fieldLabel}>
        Store location
        <Typography variant="caption" color="textSecondary">
         &nbsp; Zip Code
        </Typography>
      </Label>
      <FormControl className={!!watchedValue && !open ? css.hidden : css.input}>
        <Controller
          name={name}
          render={({ field }) => (
            <Autocomplete
              {...field}
              id={id}
              open={open}
              options={options}
              loading={loading}
              blurOnSelect={false}
              size={size}
              openOnFocus
              autoComplete
              loadingText={loadingText}
              noOptionsText={value?.length >= MINIMUM_ZIPCODE_LENGTH ? noOptionsText : 'Enter a Zip Code to search for stores'}
              classes={{
                option: css.option,
                root: css.autoComplete
              }}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              onFocus={() => setOpen(true)} // always keep open on focus
              onBlur={() => setOpen(false)}
              isOptionEqualToValue={(o: StoreAddress) => o?.address === field.value.address}
              getOptionLabel={(o: StoreAddress) => o?.address || ''}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event?.key === 'Enter') {
                  // prevent outer form submission on keyboard selection with enter key
                  event?.preventDefault();
                }
              }}
              onChange={(_, data: StoreAddress | null) => {
                setValue(data?.address ?? '');
                field.onChange(data);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={id}
                  name={name}
                  variant={variant}
                  inputRef={inputRef}
                  InputProps={{
                    ...params.InputProps,
                    value,
                    classes: {
                      root: !watchedValue && value ? css.required : ''
                    },
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                  inputProps={{
                    ...params.inputProps,
                    id,
                    name,
                    value,
                    'data-testid': name,
                    onChange: handleInputChange
                  }}
                />
              )}
              renderOption={(props, option: StoreAddress) => (
                <address className={css.address}>
                  <Typography component="label">
                    {option?.address}
                    <br />
                    {option?.city}, {option?.state} {option?.zipcode}
                  </Typography>
                  <Button
                    color="secondary"
                    size="small"
                    endIcon={<ChevronRight color="action" />}
                    className={css.button}
                    onClick={() => {
                      setValue(option?.address || '');
                      onSelectStore?.(option);
                      field?.onChange(option);
                      setOpen(false);
                    }}
                  >
                    SELECT
                  </Button>
                </address>
              )}
            />
          )}
          rules={{ required }}
          control={baseControl}
          defaultValue={defaultValue || ''}
        />
      </FormControl>
      {watchedValue ? (
        <Card className={css.selected}>
          <address className={`${css.address}`}>
            <div>
              <Typography component="label">{watchedValue?.address}</Typography>
              <br />
              <Typography component="label">{watchedValue?.city}, {watchedValue?.state} {watchedValue?.zipcode}</Typography>
            </div>
            <Button
              color="secondary"
              size="small"
              variant="text"
              classes={{ root: css.button }}
              endIcon={<ChevronRight color="action" />}
              onClick={() => {
                setOpen(true);

                // focus and restore zipcode value
                if (inputRef?.current) {
                  setValue(watchedValue?.zipcode || '');

                  setTimeout(() => {
                  // make sure the hidden class is removed before focusing
                    inputRef?.current?.focus?.();
                  }, 0);
                }
              }}
            >
              CHANGE
            </Button>
          </address>
        </Card>
      ) : null}
    </>
  );

}
